import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import type { TokensUsageLast6Months } from '../../types/dashboard';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

interface TokensUsage6MonthsChartProps {
  data: TokensUsageLast6Months;
}

export default function TokensUsage6MonthsChart({ data }: TokensUsage6MonthsChartProps) {
  const formatTokens = (tokens: number): string => {
    return `${tokens}M`;
  };

  const chartData = {
    labels: data.data.map(item => {
      const [year, month] = item.year_month.split('-');
      const monthNames = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];
      return `${monthNames[parseInt(month) - 1]}/${year}`;
    }),
    datasets: [
      {
        label: 'Tokens de Entrada',
        data: data.data.map(item => item.input_tokens),
        backgroundColor: 'rgba(34, 197, 94, 0.8)',
        borderColor: 'rgb(34, 197, 94)',
        borderWidth: 1,
      },
      {
        label: 'Tokens de Saída',
        data: data.data.map(item => item.output_tokens),
        backgroundColor: 'rgba(249, 115, 22, 0.8)',
        borderColor: 'rgb(249, 115, 22)',
        borderWidth: 1,
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: true,
        position: 'top' as const,
      },
      tooltip: {
        callbacks: {
          label: (context: any) => {
            const value = context.parsed.y;
            return `${context.dataset.label}: ${formatTokens(value)} tokens`;
          },
          footer: (tooltipItems: any[]) => {
            const dataIndex = tooltipItems[0]?.dataIndex;
            if (dataIndex === undefined) return '';

            const totalValue = data.data[dataIndex]?.total_tokens || 0;
            return `Total: ${formatTokens(totalValue)} tokens`;
          },
        },
      },
    },
    scales: {
      x: {
        stacked: true,
      },
      y: {
        stacked: true,
        beginAtZero: true,
        ticks: {
          callback: (value: any) => formatTokens(value),
        },
      },
    },
  };

  const getTotalFormatted = (type: 'input' | 'output' | 'total'): string => {
    const total = data.data.reduce((sum, item) => {
      let value = 0;
      if (type === 'input') value = item.input_tokens;
      else if (type === 'output') value = item.output_tokens;
      else value = item.total_tokens;

      return sum + value;
    }, 0);

    return formatTokens(total);
  };

  return (
    <div>
      <div className="grid grid-cols-3 gap-4 mb-4">
        <div className="bg-green-50 dark:bg-green-900/20 p-3 rounded-lg">
          <div className="text-sm text-green-600 dark:text-green-400">Entrada</div>
          <div className="text-lg font-semibold text-green-900 dark:text-green-100">
            {getTotalFormatted('input')}
          </div>
        </div>
        <div className="bg-orange-50 dark:bg-orange-900/20 p-3 rounded-lg">
          <div className="text-sm text-orange-600 dark:text-orange-400">Saída</div>
          <div className="text-lg font-semibold text-orange-900 dark:text-orange-100">
            {getTotalFormatted('output')}
          </div>
        </div>
        <div className="bg-blue-50 dark:bg-blue-900/20 p-3 rounded-lg">
          <div className="text-sm text-blue-600 dark:text-blue-400">Total</div>
          <div className="text-lg font-semibold text-blue-900 dark:text-blue-100">
            {getTotalFormatted('total')}
          </div>
        </div>
      </div>
      <div className="h-80">
        <Bar data={chartData} options={options} />
      </div>
    </div>
  );
}
